import { useEffect, useState } from 'react';
import useFrameworkTranslation from 'framework/language/useFrameworkTranslation';
import {
  GET_CURRENCY_CALCULATIONS,
  GET_USER_CURRENCIES
} from '../constants/aryGraphqlSchemas';
import arySystemSettings from '../constants/arySystemSettings';
import { getSourceToDestRate } from '../utils/Utils';
import { useAryLazyQuery } from './useAryGraphql';
import useSystemSettingsContext from './useSystemSettingsContext';

const useTradeRateValidation = ({
  sourceCurrencyCode,
  destinationCurrencyCode,
  tradeRate
}) => {
  const { systemSettings } = useSystemSettingsContext();
  const lang = systemSettings.systemLanguage;
  const { t } = useFrameworkTranslation();

  const [isTradeRateValid, setIsTradeRateValid] = useState(false);
  const [sourceToDestRate, setSourceToDestRate] = useState(false);
  const [tradeRateLabel, setTradeRateLabel] = useState('');
  const [calculationRate, setCalculationRate] = useState(0);

  const outputBasedOnLang = (sourceCurrency, destinationCurrency) => {
    if (lang === 'en') {
      return `${sourceCurrency} ${t('to')} ${destinationCurrency} ${t('Rate')}`;
    }
    return `${t('Rate')} ${sourceCurrency} ${t('To')} ${destinationCurrency}`;
  };
  const getTradeRateLabel = (sourceCurrency, destinationCurrency) => {
    if (
      !sourceCurrency ||
      !destinationCurrency ||
      sourceCurrency === destinationCurrency
    ) {
      return '';
    }
    return outputBasedOnLang(sourceCurrency, destinationCurrency);
  };

  const [getUserCurrencies, { data: userCurrencies }] = useAryLazyQuery({
    gqlConfig: GET_USER_CURRENCIES,
    items: `
      code
      title
      dollarRate
      isDivision
    `
  });

  const [getCurrencyCalculations, { data: getCurrencyCalculationsQueryData }] =
    useAryLazyQuery({
      gqlConfig: GET_CURRENCY_CALCULATIONS,
      items: `
        currencyCalculations{
          sourceCurrencyCode
          destinationCurrencyCode
          calculationRate
        }
      `
    });
  useEffect(() => {
    getUserCurrencies();
    getCurrencyCalculations({
      pagination: { pageIndex: 0, pageCount: 100000 }
    });
  }, []);

  useEffect(() => {
    if (
      userCurrencies?.length &&
      sourceCurrencyCode &&
      destinationCurrencyCode
    ) {
      const sourceUserCurrency = userCurrencies.find(
        (obj) => obj.code === sourceCurrencyCode
      );
      const destinationUserCurrency = userCurrencies.find(
        (obj) => obj.code === destinationCurrencyCode
      );
      let sourceToDestCurrencyCalculation =
        getCurrencyCalculationsQueryData?.currencyCalculations.find(
          (obj) =>
            obj.sourceCurrencyCode === sourceCurrencyCode &&
            obj.destinationCurrencyCode === destinationCurrencyCode
        );
      let isDivision;
      if (sourceCurrencyCode === destinationCurrencyCode) {
        setCalculationRate(0);
      } else if (sourceToDestCurrencyCalculation) {
        isDivision = false;
        setCalculationRate(sourceToDestCurrencyCalculation.calculationRate);
      } else {
        sourceToDestCurrencyCalculation =
          getCurrencyCalculationsQueryData?.currencyCalculations.find(
            (obj) =>
              obj.sourceCurrencyCode === destinationCurrencyCode &&
              obj.destinationCurrencyCode === sourceCurrencyCode
          );
        isDivision = !!sourceToDestCurrencyCalculation;
        if (isDivision) {
          setCalculationRate(sourceToDestCurrencyCalculation.calculationRate);
        }
      }
      if (isDivision) {
        setSourceToDestRate(
          getSourceToDestRate(
            destinationUserCurrency.dollarRate,
            destinationUserCurrency.isDivision,
            sourceUserCurrency.dollarRate,
            sourceUserCurrency.isDivision
          )
        );
      } else {
        setSourceToDestRate(
          getSourceToDestRate(
            sourceUserCurrency.dollarRate,
            sourceUserCurrency.isDivision,
            destinationUserCurrency.dollarRate,
            destinationUserCurrency.isDivision
          )
        );
      }
      if (isDivision) {
        setTradeRateLabel(
          getTradeRateLabel(
            destinationUserCurrency.title,
            sourceUserCurrency.title
          )
        );
      } else {
        setTradeRateLabel(
          getTradeRateLabel(
            sourceUserCurrency.title,
            destinationUserCurrency.title
          )
        );
      }
    } else if (
      userCurrencies?.length &&
      (!sourceCurrencyCode || !destinationCurrencyCode)
    ) {
      setTradeRateLabel(getTradeRateLabel());
    }
  }, [
    userCurrencies,
    getCurrencyCalculationsQueryData,
    sourceCurrencyCode,
    destinationCurrencyCode
  ]);

  useEffect(() => {
    if (userCurrencies?.length && tradeRate && sourceToDestRate) {
      const validTolerance =
        systemSettings[arySystemSettings.profitAndLossAccuracy];
      const currentTolerance =
        (Math.abs(tradeRate / 10 ** calculationRate - sourceToDestRate) /
          sourceToDestRate) *
        100;

      setIsTradeRateValid(currentTolerance <= validTolerance);
    }
  }, [
    userCurrencies,
    getCurrencyCalculationsQueryData,
    tradeRate,
    sourceToDestRate
  ]);

  return [isTradeRateValid, tradeRateLabel];
};

export default useTradeRateValidation;
